import type { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';

import type { FeeInfo, PaySystems } from '../types/Cashier';

import { getPaySystemName, isZeroExponentCurrency } from './cashier';
import {
  TXTCLI_I_hereby_declare_that_I_have_r_ELL,
  TXTCLI_This_Xa_hrefXXN0XXN1X_aX_depos_ELL,
  TXTWCL_If_youXd_like_to_use_a_more_co_ELL,
  TXTWCL_Please_be_advised_that_a_fee_e_ELL,
  TXTWCL_Please_be_advised_that_a_fee_e_ELL_1,
  TXTWCL_Please_be_advised_that_as_info_ELL,
  TXTWCL_Please_be_advised_that_as_info_ELL_1
} from './fee.messages.json';

interface FeeMessageData {
  paySystemType: number;
  paySystemFee: FeeInfo;
  balanceCurrency: Currency;
  paySystems: PaySystems;
}

export enum FeeMessageText {
  FutureDepositFlatFee = 'FutureDepositFlatFee',
  CurrentDepositFlatFee = 'CurrentDepositFlatFee',
  FutureDepositPercentFee = 'FutureDepositPercentFee',
  CurrentDepositPercentFee = 'CurrentDepositPercentFee',
  AlternativePaySystem = 'AlternativePaySystem',
  CurrentDepositFeeFree = 'CurrentDepositFeeFree',
  FeeAcknowledge = 'FeeAcknowledge'
}

export const calculateFee = (amount: number, paySystemFee: FeeInfo): number => {
  let feeAmount = 0;

  if (!amount || !paySystemFee) {
    return feeAmount;
  }

  const { leftGraceDeposits, rate_x_100K: feeRate, minFee, maxFee } = paySystemFee;

  if (leftGraceDeposits > 0) {
    return feeAmount;
  }

  if (minFee === maxFee || !feeRate) {
    feeAmount = minFee;
  } else {
    // maxFee, minFee are in cents, that's why amount is also converted in cents
    feeAmount = Math.min(maxFee, Math.max(minFee, ((amount * 100) / 100000) * feeRate));
  }

  return feeAmount / 100;
};

export const hasFee = (amount: number, paySystemFee: FeeInfo): boolean => {
  const fee = calculateFee(amount, paySystemFee);
  return !!fee || (paySystemFee && paySystemFee.leftGraceDeposits > 0);
};

export const setFeeMessageText = (type: FeeMessageText, options: FeeMessageData): JSX.Element => {
  const { paySystemFee, paySystems, paySystemType, balanceCurrency } = options;
  const {
    alternativePaysys: alternativePaySystem,
    rate_x_100K: feeRate,
    minFee,
    maxFee
  } = paySystemFee;
  const paySystemName = getPaySystemName(paySystems, paySystemType);
  const isBalanceCurrencyZeroExponent = isZeroExponentCurrency(balanceCurrency);

  const titles = {
    [FeeMessageText.FutureDepositFlatFee]: (
      <Translation
        {...TXTWCL_Please_be_advised_that_a_fee_e_ELL}
        values={{
          0: (
            <CurrencyAmount
              value={minFee}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
            />
          ),
          1: paySystemName
        }}
      />
    ),
    [FeeMessageText.CurrentDepositFlatFee]: (
      <Translation
        {...TXTWCL_Please_be_advised_that_as_info_ELL_1}
        values={{
          0: (
            <CurrencyAmount
              value={minFee}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
            />
          ),
          1: paySystemName
        }}
      />
    ),
    [FeeMessageText.FutureDepositPercentFee]: (
      <Translation
        {...TXTWCL_Please_be_advised_that_a_fee_e_ELL_1}
        values={{
          0: feeRate / 1000,
          1: (
            <CurrencyAmount
              value={minFee}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
            />
          ),
          2: (
            <CurrencyAmount
              value={maxFee}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
            />
          ),
          3: paySystemName
        }}
      />
    ),
    [FeeMessageText.CurrentDepositPercentFee]: (
      <Translation
        {...TXTWCL_Please_be_advised_that_as_info_ELL}
        values={{
          0: feeRate / 1000,
          1: (
            <CurrencyAmount
              value={minFee}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
            />
          ),
          2: (
            <CurrencyAmount
              value={maxFee}
              currency={balanceCurrency}
              isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
            />
          ),
          3: paySystemName
        }}
      />
    ),
    [FeeMessageText.AlternativePaySystem]: (
      <Translation
        {...TXTWCL_If_youXd_like_to_use_a_more_co_ELL}
        values={{
          0: getPaySystemName(paySystems, alternativePaySystem)
        }}
      />
    ),
    [FeeMessageText.CurrentDepositFeeFree]: (
      <Translation
        {...TXTCLI_This_Xa_hrefXXN0XXN1X_aX_depos_ELL}
        values={{
          0: paySystemName
        }}
      />
    ),
    [FeeMessageText.FeeAcknowledge]: <Translation {...TXTCLI_I_hereby_declare_that_I_have_r_ELL} />
  };

  return titles[type];
};

// TODO: Refactor to reduce `Cognitive Complexity` and perhaps convert to a component
export const composeFeeMessage = (
  paySystemType: number,
  paySystemFee: FeeInfo,
  balanceCurrency: Currency,
  paySystems: PaySystems,
  bonusCode: string,
  bonusReadySystems: number[]
): JSX.Element[] => {
  const content: JSX.Element[] = [];

  if (paySystemFee) {
    const {
      alternativePaysys: alternativePaySystem,
      rate_x_100K: feeRate,
      minFee,
      maxFee,
      leftGraceDeposits
    } = paySystemFee;

    const futureFee = leftGraceDeposits > 0;
    const flatFee = minFee === maxFee || !feeRate;

    const showAlternativePaySystem =
      alternativePaySystem &&
      alternativePaySystem !== paySystemType &&
      ((bonusCode && bonusReadySystems.includes(alternativePaySystem)) || bonusCode === '');

    const options = { paySystemType, paySystemFee, balanceCurrency, paySystems };

    if (futureFee) {
      if (flatFee) {
        content.push(setFeeMessageText(FeeMessageText.FutureDepositFlatFee, options));
      } else {
        content.push(setFeeMessageText(FeeMessageText.FutureDepositPercentFee, options));
      }
      content.push(setFeeMessageText(FeeMessageText.CurrentDepositFeeFree, options));

      if (showAlternativePaySystem) {
        content.push(setFeeMessageText(FeeMessageText.AlternativePaySystem, options));
      }
      content.push(setFeeMessageText(FeeMessageText.FeeAcknowledge, options));
    } else {
      if (flatFee) {
        content.push(setFeeMessageText(FeeMessageText.CurrentDepositFlatFee, options));
      } else {
        content.push(setFeeMessageText(FeeMessageText.CurrentDepositPercentFee, options));
      }
      if (showAlternativePaySystem) {
        content.push(setFeeMessageText(FeeMessageText.AlternativePaySystem, options));
      }
    }
  }

  return content;
};
